/* eslint-disable react/prop-types */
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import LockIcon from "@mui/icons-material/Lock";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material/styles";
import axios from "@/api/axios";

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.spacing(1),
    transition: theme.transitions.create(["border-color", "box-shadow"]),
    "&:hover": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
      },
    },
    "&.Mui-focused": {
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
    },
  },
}));

export default function ChangePassword({
  open,
  onClose,
  onSuccess,
  initialOrgId = "",
  initialUsername = "",
}) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, touchedFields },
    reset,
    watch,
  } = useForm({
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: {
      orgId: initialOrgId,
      username: initialUsername,
      code: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const watchPassword = watch("newPassword");

  const getFieldValidation = (fieldName) => {
    const hasError = errors[fieldName];
    const isTouched = touchedFields[fieldName];

    if (isTouched && hasError) return "error";
    return "default";
  };

  const shouldShowError = (fieldName) => {
    return touchedFields[fieldName] && !!errors[fieldName];
  };

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      reset({
        orgId: initialOrgId,
        username: initialUsername,
        code: "",
        newPassword: "",
        confirmPassword: "",
      });
    }
  }, [open, reset, initialOrgId, initialUsername]);

  const validatePassword = (password) => {
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSymbol = /[^A-Za-z0-9]/.test(password);
    const isLongEnough = password.length >= 8;

    if (!isLongEnough) {
      return t(
        "changePassword.passwordLengthError",
        "Password must be at least 8 characters long"
      );
    }
    if (!hasUpperCase) {
      return t(
        "changePassword.passwordUpperCaseError",
        "Password must contain at least one uppercase letter"
      );
    }
    if (!hasLowerCase) {
      return t(
        "changePassword.passwordLowerCaseError",
        "Password must contain at least one lowercase letter"
      );
    }
    if (!hasNumber) {
      return t(
        "changePassword.passwordNumberError",
        "Password must contain at least one number"
      );
    }
    if (!hasSymbol) {
      return t(
        "changePassword.passwordSymbolError",
        "Password must contain at least one symbol"
      );
    }
    return true;
  };

  const validateConfirmPassword = (confirmPassword) => {
    if (confirmPassword !== watchPassword) {
      return t(
        "changePassword.passwordMismatchError",
        "Passwords do not match"
      );
    }
    return true;
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const response = await axios.post("/api/v1/auth/password-reset", {
        organization_acronym: data.orgId.trim(),
        username: data.username.trim(),
        code: data.code.trim(),
        new_password: data.newPassword,
      });

      if (response.status === 200) {
        if (onSuccess) {
          onSuccess(
            t(
              "changePassword.successMessage",
              "Your password has been successfully changed. You can now sign in with your new password."
            )
          );
        }
        reset();
        onClose();
      }
    } catch (error) {
      console.error("Password change failed:", error);
      if (onSuccess) {
        const errorMessage =
          error.response?.data?.message ||
          t(
            "changePassword.errorMessage",
            "Failed to change password. Please check your reset code and try again."
          );
        onSuccess(errorMessage, "error");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        zIndex: 1400, // Ensure it's above other dialogs
        "& .MuiDialog-paper": {
          borderRadius: 3,
          p: 1,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 600,
          fontSize: "1.5rem",
          pb: 2,
        }}
      >
        {t("changePassword.title", "Change Password")}
      </DialogTitle>

      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ display: "flex", flexDirection: "column" }}
      >
        <DialogContent sx={{ pb: 2 }}>
          <DialogContentText sx={{ mb: 3 }}>
            {t(
              "changePassword.description",
              "Check your verified email for the reset code, then enter it below along with your new password."
            )}
          </DialogContentText>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <StyledTextField
              label={t("changePassword.orgIdLabel", "Organization ID")}
              autoComplete="organization"
              type="text"
              autoFocus
              {...register("orgId", {
                required: t(
                  "changePassword.orgIdRequired",
                  "Organization ID is required"
                ),
                pattern: {
                  value: /^[A-Za-z0-9_-]+$/,
                  message: t(
                    "changePassword.orgIdPattern",
                    "Only letters, numbers, hyphens and underscores allowed"
                  ),
                },
                minLength: {
                  value: 2,
                  message: t(
                    "changePassword.orgIdMinLength",
                    "Organization ID must be at least 2 characters"
                  ),
                },
              })}
              error={shouldShowError("orgId")}
              helperText={
                shouldShowError("orgId") ? errors?.orgId?.message : ""
              }
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <BusinessIcon
                        color={
                          getFieldValidation("orgId") === "error"
                            ? "error"
                            : "action"
                        }
                      />
                    </InputAdornment>
                  ),
                },
              }}
              fullWidth
              variant="outlined"
            />

            <StyledTextField
              label={t("changePassword.usernameLabel", "Username")}
              autoComplete="username"
              type="text"
              {...register("username", {
                required: t(
                  "changePassword.usernameRequired",
                  "Username is required"
                ),
                minLength: {
                  value: 3,
                  message: t(
                    "changePassword.usernameMinLength",
                    "Username must be at least 3 characters"
                  ),
                },
                pattern: {
                  value: /^[A-Za-z0-9._@-]+$/,
                  message: t(
                    "changePassword.usernamePattern",
                    "Invalid username format"
                  ),
                },
              })}
              error={shouldShowError("username")}
              helperText={
                shouldShowError("username") ? errors?.username?.message : ""
              }
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon
                        color={
                          getFieldValidation("username") === "error"
                            ? "error"
                            : "action"
                        }
                      />
                    </InputAdornment>
                  ),
                },
              }}
              fullWidth
              variant="outlined"
            />

            <StyledTextField
              label={t("changePassword.codeLabel", "Reset Code")}
              type="text"
              {...register("code", {
                required: t(
                  "changePassword.codeRequired",
                  "Reset code is required"
                ),
                minLength: {
                  value: 1,
                  message: t(
                    "changePassword.codeMinLength",
                    "Please enter the reset code"
                  ),
                },
              })}
              error={shouldShowError("code")}
              helperText={shouldShowError("code") ? errors?.code?.message : ""}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <VpnKeyIcon
                        color={
                          getFieldValidation("code") === "error"
                            ? "error"
                            : "action"
                        }
                      />
                    </InputAdornment>
                  ),
                },
              }}
              fullWidth
              variant="outlined"
            />

            <StyledTextField
              label={t("changePassword.newPasswordLabel", "New Password")}
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              {...register("newPassword", {
                required: t(
                  "changePassword.newPasswordRequired",
                  "New password is required"
                ),
                validate: validatePassword,
              })}
              error={shouldShowError("newPassword")}
              helperText={
                shouldShowError("newPassword")
                  ? errors?.newPassword?.message
                  : ""
              }
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon
                        color={
                          getFieldValidation("newPassword") === "error"
                            ? "error"
                            : "action"
                        }
                      />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword((s) => !s)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
              fullWidth
              variant="outlined"
            />

            <StyledTextField
              label={t(
                "changePassword.confirmPasswordLabel",
                "Confirm New Password"
              )}
              type={showConfirmPassword ? "text" : "password"}
              autoComplete="new-password"
              {...register("confirmPassword", {
                required: t(
                  "changePassword.confirmPasswordRequired",
                  "Please confirm your new password"
                ),
                validate: validateConfirmPassword,
              })}
              error={shouldShowError("confirmPassword")}
              helperText={
                shouldShowError("confirmPassword")
                  ? errors?.confirmPassword?.message
                  : ""
              }
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon
                        color={
                          getFieldValidation("confirmPassword") === "error"
                            ? "error"
                            : "action"
                        }
                      />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={() => setShowConfirmPassword((s) => !s)}
                        edge="end"
                        size="small"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
              fullWidth
              variant="outlined"
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ pb: 3, px: 3, pt: 1 }}>
          <Button onClick={handleClose} disabled={isLoading}>
            {t("changePassword.cancelButton", "Cancel")}
          </Button>
          <Button
            variant="contained"
            type="submit"
            disabled={isLoading || !isValid}
            sx={{
              minWidth: 140,
              py: 1,
              fontWeight: 600,
              borderRadius: 2,
              textTransform: "none",
            }}
          >
            {isLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} color="inherit" />
                <span>
                  {t("changePassword.changingPassword", "Changing...")}
                </span>
              </Box>
            ) : (
              t("changePassword.submitButton", "Change Password")
            )}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
